import React, { createContext, useCallback, useContext, useMemo } from 'react'
import { MenuItem } from '@mui/material'
import { usePersistedState } from './api/localStore'

export type ProviderRoleId =
  | 'psychiatrist'
  | 'nutritionist'
  | 'therapist'
  | 'coach'
  | 'careCoordinator'
  | 'careLeadership'
  | 'admin'

export const PROVIDER_ROLES: { id: ProviderRoleId; label: string }[] = [
  { id: 'psychiatrist', label: 'Psychiatrist' },
  { id: 'nutritionist', label: 'Nutritionist' },
  { id: 'therapist', label: 'Therapist' },
  { id: 'coach', label: 'Coach' },
  { id: 'careCoordinator', label: 'Care Coordinator' },
  { id: 'careLeadership', label: 'Care Leadership' },
  { id: 'admin', label: 'Admin' },
]

export const labelForRole = (role: ProviderRoleId): string =>
  PROVIDER_ROLES.find((candidate) => candidate.id === role)?.label ?? role

const DEFAULT_ROLE: ProviderRoleId = 'psychiatrist'
const STORAGE_KEY = 'advanced.currentRole.v1'

type CurrentRoleState = [ProviderRoleId, (role: ProviderRoleId) => void]

const CurrentRoleContext = createContext<CurrentRoleState | null>(null)

export const CurrentRoleProvider = ({ children }: { children: React.ReactNode }) => {
  const [role, setRole] = usePersistedState<ProviderRoleId>(STORAGE_KEY, DEFAULT_ROLE)
  const set = useCallback((next: ProviderRoleId) => setRole(next), [setRole])

  return <CurrentRoleContext.Provider value={[role, set]}>{children}</CurrentRoleContext.Provider>
}

export const useCurrentRole = (): CurrentRoleState => {
  const state = useContext(CurrentRoleContext)
  if (!state) {
    throw new Error('useCurrentRole must be used within CurrentRoleProvider')
  }
  return state
}

// Pins the role for a subtree; the setter still changes the stored one.
export const CurrentRoleOverride = ({ role, children }: { role: ProviderRoleId; children: React.ReactNode }) => {
  const [, setRole] = useCurrentRole()
  const value = useMemo<CurrentRoleState>(() => [role, setRole], [role, setRole])

  return <CurrentRoleContext.Provider value={value}>{children}</CurrentRoleContext.Provider>
}

type DeveloperRoleMenuItemsProps = { onSelect: () => void }

export const DeveloperRoleMenuItems = ({ onSelect }: DeveloperRoleMenuItemsProps) => {
  const [role, setRole] = useCurrentRole()

  return (
    <>
      {PROVIDER_ROLES.map((option) => (
        <MenuItem
          key={option.id}
          selected={role === option.id}
          onClick={() => {
            setRole(option.id)
            onSelect()
          }}
        >
          View as {option.label}
        </MenuItem>
      ))}
    </>
  )
}
